// Autoria da bolha ENVIADA (from_me) — regra única dos 5 inboxes
// (wa-personal, wa-business e o shell compartilhado de Instagram, Messenger e
// widget). Antes cada shell montava o rótulo à mão e todos cortavam no
// primeiro espaço: "Agente SAC Instagram" virava "Agente". E o eco do aparelho
// (resposta dada no app do Instagram, no celular ou por outra ferramenta
// conectada à conta) saía sem autor nenhum (Bigens, 2026-10-23).
//
// Aceita os dois formatos de mensagem do front: snake_case (Cloud API,
// Instagram, Messenger, widget) e camelCase (wa-personal / Evolution).

// Eco de aparelho/app: mensagem NOSSA que não saiu pela InfinitSales. Os
// valores de `source` são os mesmos de backend/services/human_takeover_guard.py
// (_DEVICE_ECHO_SOURCES) mais os ecos de Instagram e Messenger.
export const DEVICE_ECHO_LABELS = {
  webhook: 'Pelo app do WhatsApp', // wa_personal (Evolution): celular ou WhatsApp Web
  whatsapp_app: 'Pelo app do WhatsApp', // wa_business em coexistência
  instagram_app: 'Pelo app do Instagram',
  messenger_app: 'Pelo app do Messenger',
  portal_app: 'Pelo portal',
  portal_bridge: 'Pela integração do portal',
};

const DEVICE_ECHO_TITLES = {
  webhook: 'Enviada fora da InfinitSales, direto no WhatsApp (celular ou WhatsApp Web)',
  whatsapp_app: 'Enviada fora da InfinitSales, direto no app do WhatsApp Business (celular ou WhatsApp Web)',
  instagram_app: 'Enviada fora da InfinitSales, pelo app do Instagram ou por outra ferramenta conectada à conta',
  messenger_app: 'Enviada fora da InfinitSales, pelo Messenger, pela Página ou por outra ferramenta conectada',
  portal_app: 'Enviada fora da InfinitSales, direto no portal',
  portal_bridge: 'Registrada pela integração do portal',
};

const pick = (msg, snake, camel) => {
  const a = msg[snake];
  if (a !== undefined && a !== null && a !== '') return a;
  const b = camel ? msg[camel] : undefined;
  return b !== undefined && b !== null && b !== '' ? b : null;
};

const firstName = (name) => (name ? String(name).trim().split(/\s+/)[0] : null);

export const isAgentMessage = (msg) => !!(msg && (
  msg.source === 'agent_autopilot'
  || msg.agent_response
  || pick(msg, 'originated_by_agent', 'originatedByAgent')
  || pick(msg, 'agent_id', 'agentId')
));

/**
 * Rótulo do autor de uma mensagem enviada.
 *
 * @param {object} msg
 * @param {object} [opts]
 * @param {string|null} [opts.activeAgentName] agente do canal (último recurso
 *   para mensagens antigas do agente gravadas sem agent_name)
 * @param {string|null} [opts.ownerFallbackName] nome usado quando a mensagem é
 *   humana e não tem autor gravado (os shells de WhatsApp passam o nome do
 *   usuário logado, convenção "sem sent_by = o próprio dono"; o shell
 *   compartilhado passa null: DIV-23)
 * @returns {{kind: 'agent'|'member'|'device'|'system'|null, label: string|null, title: string|null}}
 *   `label` já vem pronto para exibir: nome COMPLETO do agente (o nome do
 *   agente é o que o diferencia: "Agente SAC" x "Agente Vendas"), primeiro
 *   nome do membro (estilo Kommo) e o rótulo do app no eco.
 */
export function messageAuthor(msg, { activeAgentName = null, ownerFallbackName = null } = {}) {
  const none = { kind: null, label: null, title: null };
  if (!msg) return none;

  const sentByName = pick(msg, 'sent_by_name', 'sentByName');
  const connectionName = pick(msg, 'source_connection_name', 'sourceConnectionName');

  if (isAgentMessage(msg)) {
    const name = pick(msg, 'agent_name', 'agentName') || sentByName || activeAgentName || connectionName || 'Agente IA';
    return { kind: 'agent', label: name, title: name };
  }

  if (msg.source === 'system' || msg.type === 'system') {
    return { kind: 'system', label: 'Sistema', title: 'Mensagem do sistema' };
  }

  // Thread unificada: espelho de OUTRA conexão mostra o remetente de ORIGEM.
  if (pick(msg, 'is_mirror', 'isMirror')) {
    const name = pick(msg, 'originated_by_name', 'originatedByName') || sentByName || connectionName;
    return name ? { kind: 'member', label: firstName(name), title: name } : none;
  }

  if (sentByName) {
    return { kind: 'member', label: firstName(sentByName), title: sentByName };
  }

  // Eco do aparelho/app: rótulo honesto em vez de nada (ou do nome de quem
  // está olhando a tela).
  if (msg.source && DEVICE_ECHO_LABELS[msg.source]) {
    return { kind: 'device', label: DEVICE_ECHO_LABELS[msg.source], title: DEVICE_ECHO_TITLES[msg.source] };
  }

  if (ownerFallbackName) {
    return { kind: 'member', label: firstName(ownerFallbackName), title: ownerFallbackName };
  }
  return none;
}

export default messageAuthor;
